import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@redux/hooks";

import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseInput from "@reusables/BaseInput";
import { useGetIndustriesQuery } from "@redux/features/industries/industriesApi";
import { useGetCountriesQuery } from "@redux/api/internalApiSlice";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseButton from "@reusables/BaseButton";

import { useUpdateCompanyMutation } from "@redux/features/companies/companiesApi";
import { toast } from "react-toastify";
import BasePhoneInput from "@reusables/BasePhoneInput";
import BaseImageUploader from "@reusables/BaseImageUploader";
import { getCountryFlag } from "@helpers/utils";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { Stack, Typography } from "@mui/material";
import { AlertCircle, Printer } from "lucide-react";
import { isErrorWithMessage } from "@redux/api/query";
import { refinements } from "@helpers/refinements";
import { Can, useAbility } from "@/casl.config";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import BasePermissionBlocker from "@reusables/BasePermissionBlocker";
import BaseTooltip from "@reusables/BaseTooltip";
import { setCompany } from "@redux/features/auth/authSlice";
import { ReactComponent as CrossCircledSVG } from "@assets/icons/ic_cross_circled.svg";

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_FILE_SIZE = import.meta.env.VITE_MAX_LOGO_SIZE;
const imageDomainURLPrefix = import.meta.env.VITE_API_URL;

const formScheme = z.object({
    name: z.string().trim().nonempty().min(3).max(255),
    industry: z.object({ id: z.number(), name: z.string() }).nullish(),
    country: z.object({ id: z.number(), name: z.string(), code: z.string() }),
    street: z.string().trim().refine(refinements.alphaNumeric.refine, refinements.alphaNumeric.message),
    street_2: z.string().trim().refine(refinements.alphaNumeric.refine, refinements.alphaNumeric.message).nullish(),
    city: z.string().trim().refine(refinements.nonNumeric.refine, refinements.nonNumeric.message),
    zipcode: z.string().trim().refine(refinements.numericRequired.refine, refinements.numericRequired.message),
    phone: z.string().trim(),
    email: z.string().trim().email(),
    /** Empty string allowed; non-empty values must match domain format */
    website: z.string().trim().refine(refinements.domainName.refine, refinements.domainName.message),
    registration_number: z.string().refine(refinements.validateCRN.refine, refinements.validateCRN.message),
    base_currency: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        symbol: z.string()
    }).optional(),
    printer_email: z.string().trim().refine(refinements.email.refine, refinements.email.message).nullish(),
    logo: z.any().optional()
        .refine((file) => (file?.size ?? 0) <= MAX_FILE_SIZE, `Max image size is 1MB.`)
        .refine(
            (file) => !file || typeof(file) === "string" || ACCEPTED_IMAGE_TYPES.includes(file?.type),
            "Only .jpg, .jpeg, .png and .webp formats are supported."
        )
});

export type DetailsTabFormTyping = z.infer<typeof formScheme>;

export default function DetailsTab() {
    const { t } = useTranslation("", { keyPrefix: "settings.general.companyInformation.tabs.companyDetails" });
    const ability = useAbility();
    const dispatch = useAppDispatch();

    const selectedCompany = useAppSelector(state => state.auth.user?.company);

    const [updateCompany, { isLoading: isUpdateLoading }] = useUpdateCompanyMutation();

    const { data: industryOptions = [], isLoading: industryOptionsLoading } = useGetIndustriesQuery();
    const { data: countryOptions = [], isLoading: countryOptionsLoading } = useGetCountriesQuery();

    const { control, handleSubmit, setValue, reset, watch } = useForm<DetailsTabFormTyping>({
        resolver: zodResolver(formScheme)
    });

    const selectedCountry = useWatch({ control, name: "country" });
    const logo = useWatch({ control, name: "logo" });

    const [isModalVisible, setModalVisible] = useState(false);
    const [originalRegistrationNumber, setOriginalRegistrationNumber] = useState("");
    const [pendingFormData, setPendingFormData] = useState<DetailsTabFormTyping>();

    const companyNameInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (selectedCompany && selectedCompany.email != null) {
            setOriginalRegistrationNumber(selectedCompany.registration_number.value);
            setValue("name", selectedCompany.name);
            setValue("industry", selectedCompany.industry);
            setValue("country", selectedCompany.country);
            setValue("street", selectedCompany.street);
            setValue("street_2", selectedCompany.street_2);
            setValue("city", selectedCompany.city);
            setValue("zipcode", selectedCompany.zipcode);
            setValue("phone", selectedCompany.phone);
            setValue("email", selectedCompany.email);
            setValue("website", selectedCompany.website ?? "");
            setValue("registration_number", selectedCompany.registration_number.value);
            setValue("printer_email", selectedCompany.printer_email);
            if (selectedCompany.logo) {
                setValue("logo", imageDomainURLPrefix + selectedCompany.logo);
            }
        }
    }, [selectedCompany, setValue, reset]);

    // Main submit handler, called on creation / update submit
    const onSubmit = handleSubmit((data) => {
        // If registration number has changed, show confirmation modal (since we have only 2 attempts)
        const isRegistrationNumberChanged = data.registration_number !== originalRegistrationNumber;
        if (selectedCompany && isRegistrationNumberChanged) {
            setPendingFormData(data);
            setModalVisible(true);
            return;
        }

        // Performing mutation
        performMutation(data);
    });

    // Additional submitting, if user confirms registration number change
    const handleModalConfirm = useCallback(() => {
        if (pendingFormData) {
            performMutation(pendingFormData);
            setPendingFormData(undefined);
        }
        setModalVisible(false);
    }, [pendingFormData]);

    // Implemented as a separate function to avoid code duplication and to be able to call it during confirmation modal submit
    const performMutation = (data: DetailsTabFormTyping) => {
        if (!selectedCompany) return;

        const mutationData = {
            name: data.name,
            country: data.country?.id,
            street: data.street,
            ...(!!data.street_2 && { street_2: data.street_2 }),
            city: data.city,
            zipcode: data.zipcode,
            phone: data.phone,
            email: data.email,
            website: data.website?.trim() ?? "",
            printer_email: data.printer_email,
            ...(data.registration_number !== originalRegistrationNumber && {
                registration_number: data.registration_number
            }),

            ...(!!data.industry && {
                industry: data.industry.id
            }),

            ...(!!data.logo && typeof(data.logo) !== "string" && {
                logo: data.logo
            }),

            ...(selectedCompany?.logo && !data.logo && {
                delete_logo: 1 as const
            })
        };

        updateCompany({ id: selectedCompany.id, ...mutationData }).unwrap()
            .then((data) => {
                toast.success(t("responses.update.success"));
                setModalVisible(false);
                dispatch(setCompany(data));
            })
            .catch((e) => {
                console.error(e);

                if (isErrorWithMessage(e)) toast.error(e.message);
                else toast.error(t("responses.update.error"));
            });
    };

    const handleClearImage = () => setValue("logo", undefined);

    return (
        <>
            <Can not I="company.view" a="general">
                <NoPermissionBanner />
            </Can>

            <Can I="company.view" a="general">
                <form onSubmit={onSubmit}>
                    <div className="flex flex-col md:flex-row mb-8">
                        <div className="md:flex-grow">
                            <div className="font-semibold text-gray-600">{t("logo.label")}</div>
                            <div className="flex flex-row flex-wrap gap-4">
                                <div className="flex mt-6 space-x-4 relative">
                                    <Controller
                                        name="logo"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <div className="flex flex-column justify-center w-[120px] relative">
                                                {!!logo && (
                                                    <Can I="company.edit" a="general">
                                                        <CrossCircledSVG
                                                            className="absolute top-[-12px] right-[-12px] text-gray-600 cursor-pointer z-10"
                                                            width={24}
                                                            height={24}
                                                            onClick={() => handleClearImage()}
                                                        />
                                                    </Can>
                                                )}
                                                <BaseImageUploader
                                                    {...field}
                                                    {...fieldState}
                                                    src={logo}
                                                    error={fieldState.error}
                                                    size={{ width: 120, height: 120 }}
                                                    disabled={ability.cannot("company.edit", "general")}
                                                />
                                            </div>
                                        )}
                                    />
                                </div>
                                <div className="flex flex-column">
                                    <div className="grow mt-4 text-sm text-gray-600">
                                        <div>{t("logo.restriction.0")}</div>
                                        <div>{t("logo.restriction.1")}</div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    <BaseInputsGrid>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    ref={companyNameInputRef}
                                    error={fieldState.error}
                                    label={t("fields.companyName") + " *"}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <Controller
                            name={"registration_number"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <div>
                                    <Stack
                                        direction="row"
                                        alignItems={"center"}
                                        gap={1}
                                        className="mb-2"
                                    >
                                    <span
                                        className="text-inputs-label-dim">{t("fields.registrationNumber.label")} *</span>

                                        <BaseTooltip
                                            title={<Trans t={t} i18nKey={"fields.registrationNumber.validationRule"}
                                                          components={{ b: <b className={"underline"} /> }} />}>
                                            <div
                                                className="bg-[#D9D6DA] w-[18px] h-[18px] rounded-full flex justify-center items-center transition-all hover:bg-lightGreen-400 cursor-pointer">
                                                <AlertCircle size={12} color={"#686868"} />
                                            </div>
                                        </BaseTooltip>
                                    </Stack>
                                    <BaseInput
                                        {...field}
                                        error={fieldState.error}

                                        disabled={selectedCompany?.registration_number?.count === 0 || ability.cannot("company.edit", "general")}
                                    />
                                </div>
                            )}
                        />

                        <Controller
                            name={"industry"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    {...field}
                                    error={fieldState.error}
                                    label={t("dropdowns.industry.label")}
                                    placeholder={`${t("dropdowns.industry.placeholder")}`}

                                    options={industryOptions}
                                    isLoading={industryOptionsLoading}

                                    getter={{
                                        key: (opt) => opt.id,
                                        label: (opt) => opt.name
                                    }}

                                    autocomplete
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <Controller
                            name={"street"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.street") + " *"}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <Controller
                            name={"street_2"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.street2")}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <Controller
                            name={"zipcode"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.zipCode") + " *"}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <Controller
                            name={"city"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.city") + " *"}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <Controller
                            name={"country"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseDropdown
                                    {...field}
                                    {...fieldState}
                                    label={t("dropdowns.country.label") + " *"}
                                    placeholder={t("dropdowns.country.placeholder")}
                                    options={countryOptions}
                                    getter={{
                                        label: (opt) => opt.name,
                                        key: (opt) => opt.id,
                                        renderOption: (opt, icon) => (
                                            <div>
                                                <img src={getCountryFlag(opt.code)} width={20} alt={opt.code} />
                                                <span className="ml-[8px] grow">{opt.name}</span>
                                                {icon}
                                            </div>
                                        )
                                    }}

                                    autocomplete
                                    isLoading={countryOptionsLoading}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <Controller
                            name={"phone"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BasePhoneInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.phone") + " *"}

                                    variant="custom"
                                    size="md"
                                    country={!selectedCompany ? selectedCountry?.code : ""}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <Controller
                            name={"email"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.email") + " *"}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <Controller
                            name={"website"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.website")}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            )}
                        />

                        <div>
                            <Controller
                                name={"printer_email"}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <BaseInput
                                        {...field}
                                        error={fieldState.error}
                                        type={"email"}
                                        label={t("fields.printerEmail.label")}

                                        sx={{
                                            "& .MuiInputBase-input": {
                                                paddingRight: "38px"
                                            }
                                        }}

                                        icon={{
                                            right: {
                                                el: <Printer color={"#a6a6a6"} size={16} />,
                                                offset: "16px"
                                            }
                                        }}

                                        disabled={ability.cannot("company.edit", "general")}
                                    />
                                )}
                            />
                            <Typography variant="caption" display="block" sx={{ opacity: 0.5 }}>
                                {t("fields.printerEmail.caption")}
                            </Typography>
                        </div>
                    </BaseInputsGrid>

                    <div className="flex justify-center mt-5">
                        {(selectedCompany && originalRegistrationNumber !== watch("registration_number")) ? (
                            <BaseButton
                                text={t("buttons.confirm")}
                                size="md"
                                type="submit"
                                onClick={() => setModalVisible(true)}
                            />
                        ) : (
                            <BasePermissionBlocker action="company.edit" module="general">
                                <BaseButton
                                    text={t("buttons.save")}
                                    size="md"
                                    type="submit"
                                    loading={isUpdateLoading}
                                    disabled={ability.cannot("company.edit", "general")}
                                />
                            </BasePermissionBlocker>
                        )}
                    </div>
                </form>
            </Can>

            {isModalVisible && (
                <ConfirmationModal
                    isOpen={isModalVisible}
                    onClose={() => setModalVisible(false)}
                    onConfirm={handleModalConfirm}
                    isSelectedCompany={!!selectedCompany}
                />
            )}
        </>
    );
}